import Box from '@mui/material/Box';
import React, { useMemo } from 'react';
import DataTile from '../DataTile';
import Grid from '@mui/material/Grid';
import type {
  RepaymentPolicyInterface,
  RepaymentWaterfall,
} from '../../../types/policy';
import { GridColDef, GridValueFormatterParams } from '@mui/x-data-grid';
import ProgramDataGrid from './ProgramDataGrid';
import { useAppSelector } from '../../../store/hook';
import startsWith from 'lodash/startsWith';
import { convertCamelCase } from '../../../utils/stringUtils';

const getRepaymentPolicyTableColumns = (): GridColDef<RepaymentWaterfall>[] => [
  {
    field: 'amount_type',
    headerName: 'Amount Type',
    flex: 1,
    sortable: false,
    minWidth: 180,
    valueFormatter: (params: GridValueFormatterParams<string>) =>
      convertCamelCase(params.value),
  },
  {
    field: 'priority_order',
    headerName: 'Priority Order',
    flex: 1,
    sortable: false,
    minWidth: 180,
  },
];

function ViewRepaymentPolicy() {
  const columnsRef = useMemo(() => getRepaymentPolicyTableColumns(), []);
  const activePolicy = useAppSelector(
    (state) => state.programManagement.activePolicy
  ) as RepaymentPolicyInterface;

  const [billedData, unBilledData] = useMemo(
    () => [
      activePolicy.repayment_waterfall?.filter((data) =>
        startsWith(data.amount_type, 'BILLED')
      ),
      activePolicy.repayment_waterfall?.filter((data) =>
        startsWith(data.amount_type, 'UNBILLED')
      ),
    ],
    [activePolicy.repayment_waterfall]
  );

  return (
    <Box
      sx={{
        p: {
          xs: 1,
          sm: 3,
        },
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          gap: 3,
          flex: 1,
        }}
      >
        <DataTile
          title="Auto Debit Number of Retries"
          value={`${activePolicy?.auto_debit_number_of_retries ?? '-'}`}
        />
        <DataTile
          title="Min Number of Installments"
          value={`${
            activePolicy?.partial_pay_off_minimum_number_of_installments ?? '-'
          }`}
        />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <ProgramDataGrid
            data={billedData}
            columns={columnsRef}
            idColumn={'amount_type'}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ProgramDataGrid
            data={unBilledData}
            columns={columnsRef}
            idColumn={'amount_type'}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default ViewRepaymentPolicy;
